import { Component, ElementRef, HostListener, OnInit, ViewChild, inject, AfterViewInit } from '@angular/core';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { CdkScrollable, ScrollingModule } from '@angular/cdk/scrolling';
import { NavigationSkipped, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { Viewport } from './common/enums/viewport';
import { ViewportSizeService } from './common/services/viewport-size.service';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { MatIconRegistry } from '@angular/material/icon';
import { SessionService } from './common/services/session.service';
import { SocketService } from './common/services/socket.service';
import { TenantService, readTabTenant } from './common/services/tenant.service';
import { InstallPwaPromptComponent } from "./components/install-pwa-prompt/install-pwa-prompt.component";
import { TenantBannerComponent } from './components/tenant-banner/tenant-banner.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    MatSidenavModule,
    NavigationComponent,
    ToolbarComponent,
    RouterOutlet,
    AsyncPipe,
    ScrollingModule,
    InstallPwaPromptComponent,
    TenantBannerComponent
]
})
export class AppComponent implements OnInit, AfterViewInit {

  bodyClass = '';
  desktopViewportClass = Viewport.desktop;
  handsetViewportClass = Viewport.handset;

  @ViewChild('drawer') drawer: MatDrawer | undefined;
  @ViewChild('content') contentContainer: ElementRef<HTMLElement> | undefined;
  @ViewChild(CdkScrollable) scrollContainer!: CdkScrollable;

  private viewport = inject(ViewportSizeService);
  private router = inject(Router);
  private session = inject(SessionService);
  private socket = inject(SocketService);
  private tenant = inject(TenantService);

  isHandset$: Observable<boolean> = combineLatest([
    this.viewport.isHandset$,
    this.viewport.isTablet$
  ]).pipe(
    map(([isHandset, isTablet]) => isHandset || isTablet)
  );
  isScrolled: boolean = false;
  headerThreshold: number = 32;
  scrollTop: number = 0;
  title = 'FLOWDOX|AI'; // @todo: Remove. Not used.
  viewportClass = '';
  isLoggedIn = false;

  constructor(private iconRegistry: MatIconRegistry) {
    this.session.isLoggedIn$.pipe(distinctUntilChanged()).subscribe(loggedIn => {
      this.isLoggedIn = !!loggedIn;
      this.toggleTenantContext(!!loggedIn);
      this.toggleSocketConnection(!!loggedIn);
    });
  }

  private updateScrollState() {
    if (this.scrollContainer) {
      this.scrollTop = this.scrollContainer.measureScrollOffset('top');
      this.isScrolled = this.scrollTop >= this.headerThreshold;
      this.setBodyClass();
    }
  }

  ngOnInit() {
    this.initRoutingDetection();

    this.viewport.viewportClasses$.subscribe((classes) => {
      this.viewportClass = classes.join(' ');
    });

    this.iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
  }

  ngAfterViewInit() {
    // Initialize scrolling after view is initialized
    setTimeout(() => {
      this.updateScrollState();
    });
  }

  initRoutingDetection() {
    this.router.events.subscribe((event) => {
      if (this.drawer?.opened && event instanceof NavigationStart || event instanceof NavigationSkipped) {
        this?.drawer?.close();
      }
    });
  }

  @HostListener('body:scroll', ['$event'])
  onContainerScroll(_: Event) {
    this.updateScrollState();
  }

  setBodyClass() {
    this.bodyClass = this.isScrolled ? 'scrolled' : '';
  }

  toggleMenu() {
    this.drawer?.toggle()
  }

  toggleTenantContext(loggedIn: boolean) {
    if (loggedIn) {
      this.tenant.load().catch((err) => console.error('Failed to load organization context', err));
    } else {
      this.tenant.clear();
    }
  }

  toggleSocketConnection(loggedIn: boolean) {
    if(loggedIn) {
      this.socket.connect({
        auth: {
          token: this.session.getToken(),
          accountId: readTabTenant(),
        },
      });
    } else {
      this.socket.disconnect();
    }
  }
}
